/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _omit from "lodash/omit";

import shareAuthorizationDataService from "services/shareCapitalAuthorization";

// --- EXISTING THUNKS ---

export const addShareAuthorizationAsync = createAsyncThunk(
  "shareAuthorization/addShareAuthorizationAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAuthorizationDataService.create(data);
      if (!res.status) {
        return rejectWithValue(
          res.message || "Failed to create share authorization"
        );
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

export const updateShareAuthorizationAsync = createAsyncThunk(
  "shareAuthorization/updateShareAuthorizationAsync",
  async (data) => {
    const res = await shareAuthorizationDataService.update(
      data._id,
      _omit(data, [
        "_id",
        "_v",
        "createdAt",
        "createdBy",
        "updatedAt",
        "rightsAttached",
      ])
    );
    return res.data;
  }
);

export const deleteShareAuthorizationAsync = createAsyncThunk(
  "shareAuthorization/deleteShareAuthorizationAsync",
  async (data) => {
    const res = await shareAuthorizationDataService.remove(data?.id);
    return res.data;
  }
);

export const getShareAuthorizationsAsync = createAsyncThunk(
  "shareAuthorization/getShareAuthorizationsAsync",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await shareAuthorizationDataService.getList(companyId);

      if (!res) {
        return rejectWithValue(
          "Failed to fetch share authorizations: Invalid response structure"
        );
      }

      const authorizations = res.shares.items;
      const totalCount = res.shares.totalCount;

      return {
        items: authorizations,
        totalCount: totalCount,
      };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

// ✅ NEW SUBMIT AUTHORIZATIONS THUNK
export const submitAuthorizationsAsync = createAsyncThunk(
  "shareAuthorization/submitAuthorizationsAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAuthorizationDataService.submitAuthorizations(
        data
      );
      if (!res.status) {
        return rejectWithValue(
          res.message || "Failed to submit authorizations"
        );
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

// --- SLICE ---
const shareAuthorizationSlice = createSlice({
  name: "Proposed Share Table",
  initialState: {
    activeTab: "",
    shares: [],
    totalCount: 0,
  },
  reducers: {
    addAuthorizationShare: (state, action) => {
      state.shares.push(action.payload.authorizationShare);
    },
    updateAuthorizationShare: (state, { payload: { authorizationShare } }) => {
      const id = authorizationShare._id;
      const index = state.shares.findIndex((item) => id === item._id);
      if (index > -1) state.shares.splice(index, 1, authorizationShare);
    },
    deleteAuthorizationShare: (state, { payload }) => {
      const index = state.shares.findIndex((item) => payload._id === item._id);
      if (index > -1) state.shares.splice(index, 1);
    },
    updateAuthorizationSharesSlice: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setAuthorizationShares: (state, action) => {
      state.shares = action.payload;
    },
    resetAuthorizationSharesTable: (state) => {
      state.shares = [];
      state.totalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addShareAuthorizationAsync.fulfilled, (state, action) => {
        const newAuthorization = action.payload.authorization;
        if (newAuthorization) {
          state.shares.push(newAuthorization);
          state.totalCount += 1;
        }
      })
      .addCase(
        updateShareAuthorizationAsync.fulfilled,
        (state, { payload }) => {
          const index = state.shares.findIndex(
            (item) => payload._id === item._id
          );
          if (index > -1) {
            state.shares.splice(index, 1, { ...payload });
          }
        }
      )
      .addCase(
        deleteShareAuthorizationAsync.fulfilled,
        (state, { payload }) => {
          const index = state.shares.findIndex(
            (item) => item._id === payload.id
          );
          if (index > -1) {
            state.shares.splice(index, 1);
            state.totalCount -= 1;
          }
        }
      )
      .addCase(getShareAuthorizationsAsync.fulfilled, (state, action) => {
        state.shares = action.payload.items;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getShareAuthorizationsAsync.rejected, () => {
        // Error handling if needed
      })
      .addCase(submitAuthorizationsAsync.fulfilled, (state, action) => {
        // You may optionally update shares state if response includes updated items:
        if (action.payload.updatedAuthorizations) {
          state.shares = action.payload.updatedAuthorizations;
        }
      })
      .addCase(submitAuthorizationsAsync.rejected, (state, action) => {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[shareCapitalAuthorization] Error submitting authorizations:",
            action.payload
          );
        }
      });
  },
});

export const {
  addAuthorizationShare,
  updateAuthorizationShare,
  deleteAuthorizationShare,
  updateAuthorizationSharesSlice,
  setAuthorizationShares,
  resetAuthorizationSharesTable,
} = shareAuthorizationSlice.actions;

export default shareAuthorizationSlice.reducer;
